const REQUIRED_LABEL_REGEX = /\(required\)\s*$/i;

const withRequiredLabel = (label, required) => (
  required ? `${label} (Required)` : label
);

/* ---------------------------------------------------------------------------
 * Project Custom Fields
 *
 * Custom field columns are generated from the Holder's active (non-archived)
 * Project Custom Fields at the moment the template is downloaded / a file is
 * validated, so adding, renaming, archiving or reordering a custom field is
 * picked up on the next download with no code deployment.
 * ------------------------------------------------------------------------ */

export const CUSTOM_FIELD_KEY_PREFIX = 'custom_';

// Custom field input types, mirroring the Add/Edit Custom Field modal.
export const CUSTOM_FIELD_TYPE_TEXT = 1;
export const CUSTOM_FIELD_TYPE_DROPDOWN = 2;
export const CUSTOM_FIELD_TYPE_DECIMAL = 3;

const toBooleanFlag = (value) => (
  value === true || value === 1 || value === '1' ||
  value === 'true' || value === 'True' || value === 'TRUE'
);

// The API is returning both camelCase and PascalCase shapes across endpoints.
const pickValue = (source, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = source[keys[i]];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

export const getCustomFieldId = (customField = {}) => (
  pickValue(customField, ['id', 'CustomFieldId', 'customFieldId', 'CFId'])
);

export const getCustomFieldName = (customField = {}) => String(
  pickValue(customField, ['name', 'CustomFieldName', 'customFieldName', 'FieldName']) || ''
);

export const isCustomFieldArchived = (customField = {}) => (
  toBooleanFlag(pickValue(customField, ['Archived', 'archived', 'IsArchived', 'isArchived']))
);

export const isCustomFieldRequired = (customField = {}) => (
  toBooleanFlag(pickValue(customField, ['IsRequired', 'isRequired', 'Required', 'required']))
);

export const getCustomFieldTypeId = (customField = {}) => {
  const parsed = parseInt(pickValue(customField, ['FieldTypeId', 'fieldTypeId', 'TypeId']), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

// Dropdown options are stored as a comma separated string ("Yes, No").
export const getCustomFieldOptions = (customField = {}) => {
  const raw = pickValue(customField, ['FieldOptions', 'fieldOptions', 'Options']);
  if (Array.isArray(raw)) {
    return raw.map(option => String(option || '').trim()).filter(Boolean);
  }
  if (!raw) return [];
  return String(raw).split(',').map(option => option.trim()).filter(Boolean);
};

export const getCustomFieldDisplayOrder = (customField = {}) => {
  const parsed = parseInt(pickValue(customField, ['DisplayOrder', 'displayOrder', 'Order']), 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

/**
 * Builds the column definitions for the Holder's active custom fields.
 * Archived fields are dropped and the remaining fields keep the configured
 * display order. Header labels are the configured names, untouched.
 */
export const buildCustomFieldDefinitions = (customFields = []) => (
  (Array.isArray(customFields) ? customFields : [])
    .filter(customField => customField && !isCustomFieldArchived(customField) && getCustomFieldName(customField))
    .slice()
    .sort((a, b) => getCustomFieldDisplayOrder(a) - getCustomFieldDisplayOrder(b))
    .map(customField => {
      const typeId = getCustomFieldTypeId(customField);
      return {
        key: `${CUSTOM_FIELD_KEY_PREFIX}${getCustomFieldId(customField)}`,
        customFieldId: getCustomFieldId(customField),
        label: getCustomFieldName(customField),
        required: isCustomFieldRequired(customField),
        isCustomField: true,
        inputTypeId: typeId,
        options: getCustomFieldOptions(customField),
        format: typeId === CUSTOM_FIELD_TYPE_DROPDOWN ? 'dropdown' : 'text'
      };
    })
);

const templateSchemas = {
  1: [
    { key: 'HolderID', label: 'Holder ID', required: true, format: 'text' },
    { key: 'InsuredName', label: 'Insured Name', required: true, format: 'text' },
    { key: 'CFLegalName', label: 'Legal Name', required: false, format: 'text' },
    { key: 'Address', label: 'Address', required: true, format: 'text' },
    { key: 'Address2', label: 'Address Line 2', required: false, format: 'text' },
    { key: 'City', label: 'City', required: true, format: 'text' },
    { key: 'PostalCode', label: 'Postal Code', required: true, format: 'text' },
    { key: 'State', label: 'State', required: false, format: 'text' },
    { key: 'CountryName', label: 'Country', required: false, format: 'text' },
    { key: 'ContactName', label: 'Contact Name', required: true, format: 'text' },
    { key: 'ContactPhone', label: 'Contact Phone', required: true, format: 'phone' },
    { key: 'ContactFax', label: 'Contact Fax', required: false, format: 'phone' },
    { key: 'InsuredEmail', label: 'Contact Email', required: true, format: 'email' },
    { key: 'TaxID', label: 'Tax ID', required: false, format: 'text' },
    { key: 'VendorNumber', label: 'Vendor Number', required: false, format: 'text' },
    { key: 'Note', label: 'Note', required: false, format: 'text' }
  ],
  2: [
    { key: 'HolderId', label: 'Holder ID', required: true, format: 'text' },
    { key: 'ProjectName', label: 'Project Name', required: true, format: 'text' },
    { key: 'CFProjectName2', label: 'Optional Name', required: false, format: 'text' },
    { key: 'Manager', label: 'Account Manager', required: true, format: 'text' },
    { key: 'Address1', label: 'Address', required: false, format: 'text' },
    { key: 'Address2', label: 'Address Line 2', required: false, format: 'text' },
    { key: 'City', label: 'City', required: false, format: 'text' },
    { key: 'State', label: 'State', required: false, format: 'text' },
    { key: 'CountryId', label: 'Country', required: false, format: 'text' },
    { key: 'ZipCode', label: 'Postal Code', required: false, format: 'text' },
    { key: 'RequirementSets', label: 'Requirement Set IDs', required: true, format: 'text' },
    { key: 'CFNote', label: 'Project Note', required: false, format: 'text' },
    { key: 'AIWording', label: 'AI Wording', required: false, format: 'text' },
    { key: 'CFContactName', label: 'Contact Name', required: false, format: 'text' },
    { key: 'CFContactPhone', label: 'Contact Phone', required: false, format: 'phone' },
    { key: 'CFProjectEmail', label: 'Email Address', required: false, format: 'email' },
    { key: 'ProjectNumber', label: 'Project Number', required: false, format: 'text' },
    { key: 'Description', label: 'Project Description', required: false, format: 'text' }
  ],
  3: [
    { key: 'HolderId', label: 'Holder ID', required: true, format: 'text' },
    { key: 'InsuredId', label: 'Insured ID', required: true, format: 'text' },
    { key: 'ProjectId', label: 'Project ID', required: true, format: 'text' },
    { key: 'RequirementSetId', label: 'Requirement Set ID', required: true, format: 'text' }
  ],
  4: [
    { key: 'HolderId', label: 'Holder ID', required: true, format: 'text' },
    { key: 'RequirementSetId', label: 'Requirement Set ID', required: true, format: 'text' },
    { key: 'ProjectName', label: 'Project Name', required: true, format: 'text' },
    { key: 'CFProjectName2', label: 'Optional Name', required: false, format: 'text' },
    { key: 'Manager', label: 'Account Manager', required: true, format: 'text' },
    { key: 'Address1', label: 'Address', required: true, format: 'text' },
    { key: 'Address2', label: 'Address Line 2', required: false, format: 'text' },
    { key: 'City', label: 'City', required: true, format: 'text' },
    { key: 'State', label: 'State', required: false, format: 'text' },
    { key: 'CountryId', label: 'Country', required: false, format: 'text' },
    { key: 'ZipCode', label: 'Postal Code', required: true, format: 'text' },
    { key: 'CFNote', label: 'Project Note', required: false, format: 'text' },
    { key: 'AIWording', label: 'AI Wording', required: false, format: 'text' },
    { key: 'CFContactName', label: 'Project Contact Name', required: false, format: 'text' },
    { key: 'CFContactPhone', label: 'Project Contact Phone', required: false, format: 'phone' },
    { key: 'CFProjectEmail', label: 'Project Email Address', required: false, format: 'email' },
    { key: 'ProjectNumber', label: 'Project Number', required: false, format: 'text' },
    { key: 'Description', label: 'Project Description', required: false, format: 'text' },
    // Custom fields marker - dynamically replaced with actual custom fields
    { key: '__CUSTOM_FIELDS_START__', label: '__CUSTOM_FIELDS_START__', required: false, format: 'text', isCustomFieldMarker: true },
    { key: 'InsuredName', label: 'Insured Name', required: true, format: 'text' },
    { key: 'CFLegalName', label: 'Legal Name', required: false, format: 'text' },
    { key: 'InsuredContactName', label: 'Insured Contact Name', required: true, format: 'text' },
    { key: 'InsuredContactPhone', label: 'Insured Contact Phone', required: true, format: 'phone' },
    { key: 'InsuredContactFax', label: 'Contact Fax', required: false, format: 'phone' },
    { key: 'InsuredEmail', label: 'Insured Contact Email', required: true, format: 'email' },
    { key: 'TaxID', label: 'Tax ID', required: false, format: 'text' },
    { key: 'VendorNumber', label: 'Vendor Number', required: false, format: 'text' }
  ]
};

/**
 * Header matching key: leading/trailing whitespace is trimmed, the decorative
 * "(Required)" suffix used by the template is dropped, then the value is
 * matched exactly (case-sensitive, internal spacing preserved).
 */
const headerMatchKey = (value) => String(value === null || value === undefined ? '' : value)
  .trim()
  .replace(REQUIRED_LABEL_REGEX, '')
  .trim();

const validateByFormat = (format, rawValue = '') => {
  const value = String(rawValue || '').trim();
  if (!value) return true;

  switch (format) {
    case 'email':
      // Allow single email or pipe-delimited emails
      if (value.includes('|')) {
        // Multiple emails separated by pipe
        return value.split('|').every(email => {
          const trimmedEmail = email.trim();
          return trimmedEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
        });
      } else {
        // Single email
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }
    case 'date':
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      return !Number.isNaN(new Date(`${value}T00:00:00`).getTime());
    case 'phone':
      return /^\+?[0-9()\-.\s]{7,20}$/.test(value);
    default:
      return true;
  }
};

export const dataImportConfig = {
  templateSchemas,
  templateColumns: Object.keys(templateSchemas).reduce((acc, typeId) => {
    const fields = templateSchemas[typeId] || [];
    acc[typeId] = fields.map(field => withRequiredLabel(field.label, field.required));
    return acc;
  }, {}),
  templateColumnKeys: Object.keys(templateSchemas).reduce((acc, typeId) => {
    const fields = templateSchemas[typeId] || [];
    acc[typeId] = fields.map(field => field.key);
    return acc;
  }, {}),
  requiredLabelRegex: REQUIRED_LABEL_REGEX,
  
  /**
   * Returns field definitions for a given template type.
   *
   * - Type 2 (Projects): active Project Custom Fields are appended after all
   *   standard columns, in their configured display order.
   * - Type 4 (Single-Step PI Import): custom fields are injected at the marker position.
   *
   * @param {number} typeId - The template type ID
   * @param {Array} customFields - Array of custom field objects from the API
   * @returns {Array} Array of field definition objects
   */
  getTemplateFieldDefinitions: (typeId, customFields = []) => {
    const baseFields = templateSchemas[typeId] || [];

    // Projects: custom fields go after every standard column.
    if (Number(typeId) === 2) {
      const standardFields = baseFields.filter(f => !f.isCustomFieldMarker);
      return [...standardFields, ...buildCustomFieldDefinitions(customFields)];
    }

    // For type 4, inject custom fields at the marker position
    if (typeId === 4 && customFields && customFields.length > 0) {
      const markerIndex = baseFields.findIndex(f => f.isCustomFieldMarker);

      if (markerIndex !== -1) {
        const beforeMarker = baseFields.slice(0, markerIndex);
        const afterMarker = baseFields.slice(markerIndex + 1);
        const customFieldDefinitions = customFields.map(cf => ({
          key: `custom_${cf.id || cf.CustomFieldId}`,
          label: cf.name || cf.FieldName || cf.CustomFieldName || '',
          required: false,
          format: 'text'
        }));
        
        return [...beforeMarker, ...customFieldDefinitions, ...afterMarker];
      }
    }
    
    // Return base fields without marker for types 1-3, or if no custom fields
    return baseFields.filter(f => !f.isCustomFieldMarker);
  },

  /**
   * Resolves the column index of every template field within a file's headers.
   * Matching is by header name only, so column position is never enforced.
   * @returns {Object} field key → column index (-1 when absent)
   */
  mapTemplateFieldsToHeaderIndexes: (templateFields = [], fileHeaders = []) => {
    const headerKeys = (Array.isArray(fileHeaders) ? fileHeaders : [])
      .map(header => headerMatchKey(header));

    return (templateFields || []).reduce((acc, field) => {
      acc[field.key] = headerKeys.findIndex(headerKey => headerKey === headerMatchKey(field.label));
      return acc;
    }, {});
  },

  /**
   * Compares the uploaded header row against the Holder's current template.
   * Both lists are returned so the caller can surface a single rejection.
   * @returns {{missing: Array<string>, unrecognized: Array<string>}}
   */
  compareHeaders: (fileHeaders = [], templateFields = []) => {
    const headers = (Array.isArray(fileHeaders) ? fileHeaders : [])
      .map(header => String(header === null || header === undefined ? '' : header).trim())
      .filter(header => header !== '');
    const headerKeys = headers.map(header => headerMatchKey(header));
    const templateKeys = (templateFields || []).map(field => headerMatchKey(field.label));

    const missing = (templateFields || [])
      .filter(field => !headerKeys.includes(headerMatchKey(field.label)))
      .map(field => field.label);

    // Unknown columns and archived/renamed custom fields both land here.
    const unrecognized = headers.filter((header, index) => {
      const key = headerKeys[index];
      return key !== '' && !templateKeys.includes(key);
    });

    return {
      missing: [...new Set(missing)],
      unrecognized: [...new Set(unrecognized)]
    };
  },

  headerMatchKey,

  /**
   * Validates a custom field cell value.
   * Required fields must be non-blank after trimming, and Dropdown values must
   * be one of the field's configured input values.
   * @returns {{ok: boolean, reason?: string}}
   */
  validateCustomFieldValue: (field, rawValue) => {
    const value = String(rawValue === null || rawValue === undefined ? '' : rawValue).trim();

    if (!value) {
      // Required custom fields must be non-blank after trimming; blank optional
      // custom fields are accepted.
      return (field && field.required)
        ? { ok: false, reason: `${field.label} is required` }
        : { ok: true };
    }

    if (field && field.format === 'dropdown') {
      const options = field.options || [];
      const isConfiguredValue = options.some(option => String(option).trim() === value);
      if (!isConfiguredValue) {
        return { ok: false, reason: `${field.label}: ${value} is not recognized` };
      }
    }

    return { ok: true };
  },

  buildCustomFieldDefinitions,
  
  /**
   * Returns column headers for a given template type, with custom fields injected.
   *
   * Custom field headers are the configured names, exactly as they appear in
   * Custom Fields settings — no "(Required)" decoration is appended, so imports
   * can match them case-sensitively.
   *
   * @param {number} typeId - The template type ID
   * @param {Array} customFields - Array of custom field objects from the API
   * @returns {Array} Array of column header strings
   */
  getTemplateHeaders: (typeId, customFields = []) => {
    const fields = dataImportConfig.getTemplateFieldDefinitions(typeId, customFields);
    return fields.map(field => (
      field.isCustomField ? field.label : withRequiredLabel(field.label, field.required)
    ));
  },
  
  validateFieldValue: (field, value) => validateByFormat(field && field.format, value),

  getTemplateFilename: (typeId) => {
    const date = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
    const prefixes = {
      1: 'Insureds_Import_Template',
      2: 'Projects_Import_Template',
      3: 'Project_Insured_Connections_Template',
      4: 'Single_Step_PI_Import_Template'
    };
    return `${prefixes[typeId] || 'Import_Template'}_${date}.csv`;
  }
};
